/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAdminApplications } from "../../context/ApplicationContext";
import { useNavigate } from "react-router-dom";
import ContactApplicantModal from "../../components/ContactApplicactionModal";
import ViewResumeModal from "../../components/ViewResumeModal";
import InterviewSchedulingModal from "../../components/InterviewSchedulingModal";
import { useResumeViewer } from "../../hooks/useResumeViewer";
import Skeleton from "../../components/Skeleton";
// import {
//   SkeletonText,
//   SkeletonCard,
//   SkeletonTable,
//   SkeletonList,
//   SkeletonDashboard,
// } from "../../components/Skeleton";

// ====================== INTERFACES ======================

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subscribed: boolean;
  active?: boolean;
  deactivatedAt?: string;
  deactivationType?: string;
  createdAt?: string;
  profile?: {
    phone?: string;
    location?: string;
  };
  firstName?: string;
  lastName?: string;
}

interface Job {
  deadline: string;
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  createdAt: string;
  imageUrl?: string;
  _count?: {
    jobApplications: number;
  };
}

interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: "pending" | "reviewed" | "accepted" | "rejected" | "withdrawn";
  coverLetter?: string;
  resumeUrl?: string;
  createdAt: string;
  updatedAt: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary: string;
    type: string;
    category: string;
    deadline?: string;
    createdAt: string;
    postedBy: {
      id: string;
      name: string;
      email: string;
    };
  };
  user: {
    id: string;
    name: string;
    email: string;
    profile?: {
      phone?: string;
      location?: string;
    };
  };
}

interface JobAlert {
  id: string;
  name: string;
  category?: string;
  jobType?: string;
  location?: string;
  keywords: string[];
  frequency: "instant" | "daily" | "weekly";
  isActive: boolean;
  lastTriggered?: string;
  triggerCount: number;
  userId: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ContactData {
  subject: string;
  message: string;
  sendEmail: boolean;
  applicantEmail?: string;
  applicantName?: string;
  jobTitle?: string;
  companyName?: string;
  currentUserName?: string;
}

interface InterviewData {
  date: string;
  type: string;
  location?: string;
  meetingLink?: string;
  notes?: string;
  sendEmail?: boolean;
}

interface DashboardStats {
  totalUsers: number;
  totalJobs: number;
  totalApplications: number;
  totalAlerts: number;
  pendingApplications: number;
  activeUsers: number;
  expiredJobs: number;
  activeAlerts: number;
  expiringSoonJobs: number;
}

type ActiveTab = "dashboard" | "users" | "jobs" | "applications" | "alerts";
type ApplicationStatus = Application["status"] | "all";

const isValidApplicationStatus = (
  status: string,
): status is Application["status"] => {
  return ["pending", "reviewed", "accepted", "rejected", "withdrawn"].includes(
    status,
  );
};

// ====================== UTILITY COMPONENTS ======================

const ResponsiveGrid = ({
  children,
  cols = { mobile: 1, tablet: 2, desktop: 3 },
  gap = "gap-3 sm:gap-4",
  className = "",
}: {
  children: React.ReactNode;
  cols?: { mobile: number; tablet: number; desktop: number };
  gap?: string;
  className?: string;
}) => (
  <div
    className={`grid grid-cols-${cols.mobile} sm:grid-cols-${cols.tablet} lg:grid-cols-${cols.desktop} ${gap} ${className}`}
  >
    {children}
  </div>
);

const ResponsiveCard = ({
  children,
  className = "",
  padding = "p-4 sm:p-6",
}: {
  children: React.ReactNode;
  className?: string;
  padding?: string;
}) => (
  <div
    className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${padding} ${className}`}
  >
    {children}
  </div>
);

const LoadingSpinner = ({
  size = "md",
  className = "",
}: {
  size?: "sm" | "md" | "lg";
  className?: string;
}) => {
  const sizeClasses = { sm: "h-4 w-4", md: "h-6 w-6", lg: "h-8 w-8" };
  return (
    <div
      className={`animate-spin rounded-full border-b-2 border-green-500 ${sizeClasses[size]} ${className}`}
    ></div>
  );
};

const StatCard = ({
  title,
  value,
  icon,
  color = "blue",
  trend,
  className = "",
}: {
  title: string;
  value: number | string;
  icon: string;
  color?: "blue" | "green" | "red" | "yellow" | "purple";
  trend?: { value: number; isPositive: boolean };
  className?: string;
}) => {
  const colorClasses = {
    blue: "from-blue-500 to-cyan-500",
    green: "from-green-500 to-emerald-500",
    red: "from-red-500 to-pink-500",
    yellow: "from-yellow-500 to-amber-500",
    purple: "from-purple-500 to-indigo-500",
  };

  return (
    <ResponsiveCard className={`relative overflow-hidden ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            {title}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {value}
          </p>
          {trend && (
            <p
              className={`text-xs mt-1 ${trend.isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
            >
              {trend.isPositive ? "↗" : "↘"} {Math.abs(trend.value)}%
            </p>
          )}
        </div>
        <div
          className={`p-3 rounded-full bg-gradient-to-r ${colorClasses[color]} bg-opacity-10`}
        >
          <span className="text-2xl">{icon}</span>
        </div>
      </div>
    </ResponsiveCard>
  );
};

// ====================== SKELETON COMPONENTS ======================

const StatCardSkeleton = () => (
  <ResponsiveCard className="relative overflow-hidden">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <Skeleton variant="text" width="40%" className="mb-1" />
        <Skeleton variant="text" width="60%" className="h-8 mb-1" />
        <Skeleton variant="text" width="30%" />
      </div>
      <Skeleton variant="circle" width={48} height={48} />
    </div>
  </ResponsiveCard>
);

const ApplicationCardSkeleton = () => (
  <div className="p-4 sm:p-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
    <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <Skeleton variant="text" width="70%" className="h-6 mb-2" />
            <Skeleton variant="text" width="40%" />
          </div>
          <Skeleton variant="text" width={80} height={24} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
          <div className="space-y-2">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="50%" />
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="60%" />
          </div>
        </div>

        <Skeleton variant="text" width="90%" className="mb-2" />
        <Skeleton variant="text" width="80%" />
      </div>

      <div className="flex flex-col gap-3 min-w-[250px]">
        <div className="flex items-center gap-2">
          <Skeleton variant="text" width={16} height={16} />
          <Skeleton variant="text" width={60} />
        </div>
        <Skeleton variant="input" className="w-full" />
        <div className="flex gap-2">
          <Skeleton variant="button" className="flex-1" />
          <Skeleton variant="button" className="flex-1" />
        </div>
        <div className="flex gap-2">
          <Skeleton variant="button" className="flex-1" />
          <Skeleton variant="button" className="flex-1" />
        </div>
      </div>
    </div>
  </div>
);

const UserCardSkeleton = () => (
  <div className="p-4 sm:p-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
    <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <Skeleton variant="text" width="60%" className="h-6 mb-2" />
            <Skeleton variant="text" width="80%" />
          </div>
          <div className="flex flex-col items-end gap-2">
            <Skeleton variant="text" width={60} height={24} />
            <Skeleton variant="text" width={70} height={24} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="50%" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 min-w-[200px]">
        <Skeleton variant="input" className="w-full" />
        <div className="flex gap-2">
          <Skeleton variant="button" className="flex-1" />
          <Skeleton variant="button" className="flex-1" />
        </div>
        <Skeleton variant="text" width="100%" height={16} />
      </div>
    </div>
  </div>
);

const JobCardSkeleton = () => (
  <div className="p-4 sm:p-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
    <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
      <div className="flex items-start gap-4 flex-1">
        <Skeleton
          variant="rectangle"
          width={64}
          height={64}
          className="rounded-lg"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <Skeleton variant="text" width="70%" className="h-6 mb-2" />
              <Skeleton variant="text" width="50%" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton variant="text" width={60} height={24} />
              <Skeleton variant="text" width={80} height={20} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 text-sm">
            <div className="space-y-2">
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
            </div>
            <div className="space-y-2">
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="50%" />
            </div>
            <div className="space-y-2">
              <Skeleton variant="text" width="50%" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 min-w-[200px]">
        <Skeleton variant="button" className="w-full" />
        <Skeleton variant="button" className="w-full" />
        <Skeleton variant="button" className="w-full" />
      </div>
    </div>
  </div>
);

const AlertCardSkeleton = () => (
  <div className="p-4 sm:p-6 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750">
    <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <Skeleton variant="text" width="60%" className="h-6 mb-2" />
            <Skeleton variant="text" width="40%" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton variant="text" width={60} height={24} />
            <Skeleton variant="text" width={70} height={24} />
            <Skeleton variant="text" width={80} height={24} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="50%" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 min-w-[200px]">
        <Skeleton variant="button" className="w-full" />
        <Skeleton variant="button" className="w-full" />
      </div>
    </div>
  </div>
);

// ====================== UTILITY FUNCTIONS ======================

const isJobExpiringSoon = (
  deadline: string,
  daysThreshold: number = 3,
): boolean => {
  if (!deadline) return false;
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const timeDiff = deadlineDate.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return daysDiff <= daysThreshold && daysDiff >= 0;
};

const getDaysUntilDeadline = (deadline: string): number => {
  if (!deadline) return -1;
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const timeDiff = deadlineDate.getTime() - now.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatKeywords = (keywords: string[]): string => {
  if (!keywords || keywords.length === 0) return "None";
  return keywords.slice(0, 3).join(", ") + (keywords.length > 3 ? "..." : "");
};

// ====================== MAIN COMPONENT ======================

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const { isDark } = useDarkMode();
  const navigate = useNavigate();

  // Use the enhanced admin context
  const adminApplications = useAdminApplications();
  const {
    fetchAllApplications,
    updateApplicationStatus,
    deleteApplication,
    deleteMultipleApplications,
    contactApplicant,
    scheduleInterview,
    triggerJobAlerts,
    triggerBulkJobAlerts,
    fetchJobs,
    deleteJob,
    fetchUsers,
    updateUserRole,
    activateUser,
    deactivateUser,
    deleteUser,
    fetchJobAlerts,
    updateJobAlertStatus,
    deleteJobAlert,
    calculateDashboardStats,
  } = adminApplications;

  // Resume Viewer Hook
  const resumeViewer = useResumeViewer();
  const {
    isModalOpen: isResumeModalOpen,
    currentResumeUrl,
    currentApplicantName,
    openResume,
    closeResume,
  } = resumeViewer;

  // State Management
  const [users, setUsers] = useState<User[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [allApplications, setAllApplications] = useState<Application[]>([]);
  const [jobAlerts, setJobAlerts] = useState<JobAlert[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>("dashboard");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  // Application States
  const [applicationStatusFilter, setApplicationStatusFilter] =
    useState<ApplicationStatus>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedApplications, setSelectedApplications] = useState<Set<string>>(
    new Set(),
  );

  // Loading States for Individual Operations
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deletingJobs, setDeletingJobs] = useState<Set<string>>(new Set());
  const [updatingRoles, setUpdatingRoles] = useState<Set<string>>(new Set());
  const [deletingUsers, setDeletingUsers] = useState<Set<string>>(new Set());
  const [activatingUsers, setActivatingUsers] = useState<Set<string>>(
    new Set(),
  );
  const [deactivatingUsers, setDeactivatingUsers] = useState<Set<string>>(
    new Set(),
  );
  const [triggeringAlerts, setTriggeringAlerts] = useState<Set<string>>(
    new Set(),
  );
  const [updatingAlerts, setUpdatingAlerts] = useState<Set<string>>(new Set());
  const [deletingAlerts, setDeletingAlerts] = useState<Set<string>>(new Set());
  const [bulkTriggering, setBulkTriggering] = useState(false);
  const [refreshingAlerts, setRefreshingAlerts] = useState(false);
  const [isDeletingApplications, setIsDeletingApplications] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSchedulingInterview, setIsSchedulingInterview] = useState(false);

  // Modal States
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalJobs: 0,
    totalApplications: 0,
    totalAlerts: 0,
    pendingApplications: 0,
    activeUsers: 0,
    expiredJobs: 0,
    activeAlerts: 0,
    expiringSoonJobs: 0,
  });

  // Computed filtered applications
  const filteredApplications = useMemo(() => {
    let filtered = allApplications;

    // Status filter
    if (applicationStatusFilter !== "all") {
      filtered = filtered.filter(
        (app) => app.status === applicationStatusFilter,
      );
    }

    // Search filter
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(
        (application) =>
          application.user?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          application.user?.email
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          application.job.title
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          application.job.company
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          application.status.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return filtered;
  }, [allApplications, applicationStatusFilter, searchTerm]);

  // Initialize data - FIXED: Proper array extraction
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        const [usersData, jobsData, applicationsData, alertsData] =
          await Promise.all([
            fetchUsers(),
            fetchJobs(),
            fetchAllApplications(),
            fetchJobAlerts(),
          ]);

        // FIXED: Safe array extraction with fallbacks
        setUsers(Array.isArray(usersData) ? usersData : []);
        setJobs(Array.isArray(jobsData) ? jobsData : jobsData?.jobs || []);
        setAllApplications(
          Array.isArray(applicationsData) ? applicationsData : [],
        );
        setJobAlerts(
          Array.isArray(alertsData) ? alertsData : alertsData?.alerts || [],
        );
      } catch (error) {
        console.error("Error initializing data:", error);
        setMessage("Failed to load initial data");
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [fetchUsers, fetchJobs, fetchAllApplications, fetchJobAlerts]);

  // Update dashboard stats when data changes - FIXED: Safe data handling
  useEffect(() => {
    try {
      const stats = calculateDashboardStats(
        users,
        jobs,
        allApplications,
        jobAlerts,
      );
      setDashboardStats(stats);
    } catch (error) {
      console.error("Error calculating dashboard stats:", error);
      // Set safe default stats
      setDashboardStats({
        totalUsers: 0,
        totalJobs: 0,
        totalApplications: 0,
        totalAlerts: 0,
        pendingApplications: 0,
        activeUsers: 0,
        expiredJobs: 0,
        activeAlerts: 0,
        expiringSoonJobs: 0,
      });
    }
  }, [users, jobs, allApplications, jobAlerts, calculateDashboardStats]);

  // ========== APPLICATION MANAGEMENT HANDLERS ==========

  const handleUpdateApplicationStatus = async (
    applicationId: string,
    newStatus: string,
  ) => {
    if (!isValidApplicationStatus(newStatus)) {
      setMessage("Invalid application status");
      return;
    }

    try {
      setUpdatingStatus(applicationId);
      const result = await updateApplicationStatus(applicationId, newStatus);
      if (result.success) {
        setMessage("Application status updated successfully");
        // Refresh applications
        const applications = await fetchAllApplications();
        setAllApplications(applications);
      } else {
        setMessage(result.error || "Failed to update application status");
      }
    } catch (error: any) {
      setMessage(error.message || "Error updating application status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDeleteSingleApplication = async (
    applicationId: string,
  ): Promise<void> => {
    if (!confirm("Are you sure you want to delete this application?")) return;
    try {
      const result = await deleteApplication(applicationId);
      if (result.success) {
        setMessage("Application deleted successfully");
        setAllApplications((prev) =>
          prev.filter((app) => app.id !== applicationId),
        );
        setSelectedApplications((prev) => {
          const newSet = new Set(prev);
          newSet.delete(applicationId);
          return newSet;
        });
      } else {
        setMessage(result.error || "Error deleting application");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deleting application");
    }
  };

  const handleDeleteSelectedApplications = async (): Promise<void> => {
    if (selectedApplications.size === 0) {
      setMessage("Please select applications to delete");
      return;
    }
    if (
      !confirm(
        `Are you sure you want to delete ${selectedApplications.size} application(s)?`,
      )
    )
      return;

    try {
      setIsDeletingApplications(true);
      const result = await deleteMultipleApplications(
        Array.from(selectedApplications),
      );
      if (result.success) {
        setMessage(
          `Successfully deleted ${selectedApplications.size} application(s)`,
        );
        setSelectedApplications(new Set());
        const applications = await fetchAllApplications();
        setAllApplications(applications);
      } else {
        setMessage(result.error || "Error deleting applications");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deleting applications");
    } finally {
      setIsDeletingApplications(false);
    }
  };

  const handleRefreshApplications = async (): Promise<void> => {
    try {
      setIsRefreshing(true);
      const applications = await fetchAllApplications();
      setAllApplications(applications);
      setMessage("Applications refreshed successfully");
    } catch (error: any) {
      console.error("Error refreshing applications:", error);
      setMessage("Error refreshing applications");
    } finally {
      setIsRefreshing(false);
    }
  };

  // ========== CONTACT & INTERVIEW HANDLERS ==========

  const handleOpenContactModal = (application: Application) => {
    setSelectedApplication(application);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedApplication(null);
  };

  const handleContactApplicant = async (contactData: ContactData) => {
    if (!selectedApplication) return;

    try {
      const result = await contactApplicant(
        selectedApplication.id,
        contactData,
      );
      if (result.success) {
        setMessage("Message sent successfully!");
        setContactModalOpen(false);
      } else {
        setMessage(result.error || "Failed to send message");
      }
    } catch (error: any) {
      setMessage(error.message || "Failed to send message");
    }
  };

  const handleScheduleInterview = (application: Application) => {
    setSelectedApplication(application);
    setIsInterviewModalOpen(true);
  };

  const handleCloseInterviewModal = () => {
    setIsInterviewModalOpen(false);
    setSelectedApplication(null);
  };

  const handleInterviewSchedule = async (interviewData: InterviewData) => {
    if (!selectedApplication) return;

    try {
      setIsSchedulingInterview(true);
      const result = await scheduleInterview(
        selectedApplication.id,
        interviewData,
      );
      if (result.success) {
        setMessage(
          result.emailSent
            ? "Interview scheduled and invitation sent successfully!"
            : `Interview scheduled but ${result.emailError || "email notification failed"}`,
        );
        setIsInterviewModalOpen(false);
        setSelectedApplication(null);

        // Refresh applications
        const applications = await fetchAllApplications();
        setAllApplications(applications);
      } else {
        setMessage(result.error || "Failed to schedule interview");
      }
    } catch (error: any) {
      setMessage(error.message || "Failed to schedule interview");
    } finally {
      setIsSchedulingInterview(false);
    }
  };

  // ========== USER MANAGEMENT HANDLERS ==========

  const handleUpdateUserRole = async (
    userId: string,
    newRole: string,
  ): Promise<void> => {
    try {
      setUpdatingRoles((prev) => new Set(prev).add(userId));
      const result = await updateUserRole(userId, newRole);
      if (result.success) {
        setMessage("Role updated successfully");
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId ? { ...user, role: newRole } : user,
          ),
        );
      } else {
        setMessage(result.error || "Error updating role");
      }
    } catch (error: any) {
      setMessage(error.message || "Error updating role");
    } finally {
      setUpdatingRoles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleActivateUser = async (userId: string): Promise<void> => {
    if (!confirm("Are you sure you want to activate this user?")) return;
    try {
      setActivatingUsers((prev) => new Set(prev).add(userId));
      const result = await activateUser(userId);
      if (result.success) {
        setMessage("User activated successfully");
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  active: true,
                  deactivatedAt: undefined,
                  deactivationType: undefined,
                }
              : user,
          ),
        );
      } else {
        setMessage(result.error || "Error activating user");
      }
    } catch (error: any) {
      setMessage(error.message || "Error activating user");
    } finally {
      setActivatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleDeactivateUser = async (userId: string): Promise<void> => {
    if (!confirm("Are you sure you want to deactivate this user?")) return;
    try {
      setDeactivatingUsers((prev) => new Set(prev).add(userId));
      const result = await deactivateUser(userId);
      if (result.success) {
        setMessage("User deactivated successfully");
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  active: false,
                  deactivatedAt: new Date().toISOString(),
                  deactivationType: "admin",
                }
              : user,
          ),
        );
      } else {
        setMessage(result.error || "Error deactivating user");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deactivating user");
    } finally {
      setDeactivatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleDeleteUser = async (userId: string): Promise<void> => {
    if (
      !confirm(
        "Are you sure you want to delete this user? This action cannot be undone.",
      )
    )
      return;
    try {
      setDeletingUsers((prev) => new Set(prev).add(userId));
      const result = await deleteUser(userId);
      if (result.success) {
        setMessage("User deleted successfully");
        setUsers((prev) => prev.filter((user) => user.id !== userId));
        setAllApplications((prev) =>
          prev.filter((app) => app.user.id !== userId),
        );
      } else {
        setMessage(result.error || "Error deleting user");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deleting user");
    } finally {
      setDeletingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // ========== JOB MANAGEMENT HANDLERS ==========

  const handleDeleteJob = async (jobId: string): Promise<void> => {
    if (!confirm("Are you sure you want to delete this job?")) return;
    try {
      setDeletingJobs((prev) => new Set(prev).add(jobId));
      const result = await deleteJob(jobId);
      if (result.success) {
        setMessage("Job deleted successfully");
        setJobs((prev) => prev.filter((job) => job.id !== jobId));
        const applications = await fetchAllApplications();
        setAllApplications(applications);
      } else {
        setMessage(result.error || "Error deleting job");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deleting job");
    } finally {
      setDeletingJobs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleEditJob = (jobId: string) => {
    try {
      navigate(`/dashboard/edit-job/${jobId}`);
    } catch (error) {
      console.error("Error navigating to edit job:", error);
      setMessage("Failed to load job for editing");
    }
  };

  const handleTriggerJobAlerts = async (jobId: string): Promise<void> => {
    try {
      setTriggeringAlerts((prev) => new Set(prev).add(jobId));
      const result = await triggerJobAlerts(jobId);
      if (result.success) {
        setMessage(
          `Job alerts triggered successfully! Sent ${result.sent || 0} notifications`,
        );
        const alertsData = await fetchJobAlerts();
        setJobAlerts(alertsData.alerts || []);
      } else {
        setMessage(result.error || "Error triggering job alerts");
      }
    } catch (error: any) {
      setMessage(error.message || "Error triggering job alerts");
    } finally {
      setTriggeringAlerts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleTriggerBulkJobAlerts = async (): Promise<void> => {
    if (
      !confirm(
        "Are you sure you want to trigger job alerts for all active jobs?",
      )
    )
      return;
    try {
      setBulkTriggering(true);
      const result = await triggerBulkJobAlerts();
      if (result.success) {
        setMessage(
          `Bulk job alerts completed! ${result.totalSent} notifications sent, ${result.totalFailed} failed`,
        );
        const alertsData = await fetchJobAlerts();
        setJobAlerts(alertsData.alerts || []);
      } else {
        setMessage(result.error || "Error triggering bulk job alerts");
      }
    } catch (error: any) {
      setMessage(error.message || "Error triggering bulk job alerts");
    } finally {
      setBulkTriggering(false);
    }
  };

  // ========== JOB ALERTS HANDLERS ==========

  const handleRefreshJobAlerts = async (): Promise<void> => {
    try {
      setRefreshingAlerts(true);
      const alertsData = await fetchJobAlerts();
      setJobAlerts(alertsData.alerts || []);
      setMessage("Job alerts refreshed successfully");
    } catch (error: any) {
      console.error("Error refreshing job alerts:", error);
      setMessage("Error refreshing job alerts");
    } finally {
      setRefreshingAlerts(false);
    }
  };

  const handleUpdateJobAlertStatus = async (
    alertId: string,
    isActive: boolean,
  ): Promise<void> => {
    try {
      setUpdatingAlerts((prev) => new Set(prev).add(alertId));
      const result = await updateJobAlertStatus(alertId, isActive);
      if (result.success) {
        setMessage(
          `Job alert ${isActive ? "activated" : "deactivated"} successfully`,
        );
        setJobAlerts((prev) =>
          prev.map((alert) =>
            alert.id === alertId ? { ...alert, isActive } : alert,
          ),
        );
      } else {
        setMessage(result.error || "Error updating job alert");
      }
    } catch (error: any) {
      setMessage(error.message || "Error updating job alert");
    } finally {
      setUpdatingAlerts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(alertId);
        return newSet;
      });
    }
  };

  const handleDeleteJobAlert = async (alertId: string): Promise<void> => {
    if (!confirm("Are you sure you want to delete this job alert?")) return;
    try {
      setDeletingAlerts((prev) => new Set(prev).add(alertId));
      const result = await deleteJobAlert(alertId);
      if (result.success) {
        setMessage("Job alert deleted successfully");
        setJobAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      } else {
        setMessage(result.error || "Error deleting job alert");
      }
    } catch (error: any) {
      setMessage(error.message || "Error deleting job alert");
    } finally {
      setDeletingAlerts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(alertId);
        return newSet;
      });
    }
  };

  // ========== UTILITY FUNCTIONS ==========

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        color: isDark
          ? "bg-yellow-900/50 text-yellow-300 border-yellow-700"
          : "bg-yellow-100 text-yellow-800 border-yellow-200",
        text: "Pending",
        icon: "⏳",
      },
      reviewed: {
        color: isDark
          ? "bg-blue-900/50 text-blue-300 border-blue-700"
          : "bg-blue-100 text-blue-800 border-blue-200",
        text: "Reviewing",
        icon: "👀",
      },
      accepted: {
        color: isDark
          ? "bg-green-900/50 text-green-300 border-green-700"
          : "bg-green-100 text-green-800 border-green-200",
        text: "Accepted",
        icon: "✅",
      },
      rejected: {
        color: isDark
          ? "bg-red-900/50 text-red-300 border-red-700"
          : "bg-red-100 text-red-800 border-red-200",
        text: "Rejected",
        icon: "❌",
      },
      withdrawn: {
        color: isDark
          ? "bg-gray-700 text-gray-300 border-gray-600"
          : "bg-gray-100 text-gray-800 border-gray-200",
        text: "Withdrawn",
        icon: "↩️",
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}
      >
        <span className="text-xs">{config.icon}</span>
        <span className="hidden xs:inline">{config.text}</span>
      </span>
    );
  };

  const handleSelectApplication = (applicationId: string): void => {
    setSelectedApplications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(applicationId)) {
        newSet.delete(applicationId);
      } else {
        newSet.add(applicationId);
      }
      return newSet;
    });
  };

  const handleSelectAllApplications = (): void => {
    if (selectedApplications.size === filteredApplications.length) {
      // If all are selected, deselect all
      setSelectedApplications(new Set());
    } else {
      // Select all filtered applications
      const allApplicationIds = filteredApplications.map((app) => app.id);
      setSelectedApplications(new Set(allApplicationIds));
    }
  };

  const handleApplicationClick = (applicationId: string): void => {
    setActiveTab("applications");
    // Scroll to the specific application
    setTimeout(() => {
      const element = document.getElementById(`application-${applicationId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        // Add highlight effect
        element.classList.add("bg-yellow-100", "dark:bg-yellow-900/20");
        setTimeout(() => {
          element.classList.remove("bg-yellow-100", "dark:bg-yellow-900/20");
        }, 3000);
      }
    }, 100);
  };

  // ========== RENDER SKELETON LOADERS ==========

  const renderSkeletonDashboard = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <ResponsiveGrid cols={{ mobile: 2, tablet: 2, desktop: 4 }} gap="gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <StatCardSkeleton key={index} />
        ))}
      </ResponsiveGrid>

      {/* Additional Stats */}
      <ResponsiveGrid cols={{ mobile: 2, tablet: 2, desktop: 4 }} gap="gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <StatCardSkeleton key={index} />
        ))}
      </ResponsiveGrid>

      {/* Quick Actions Skeleton */}
      <ResponsiveCard>
        <Skeleton variant="text" width="30%" className="h-6 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="button" className="h-12" />
          ))}
        </div>
      </ResponsiveCard>

      {/* Recent Activity Skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Applications Skeleton */}
        <ResponsiveCard>
          <Skeleton variant="text" width="40%" className="h-6 mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3"
              >
                <div className="flex-1 space-y-2">
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton variant="text" width={60} height={24} />
                  <Skeleton variant="text" width={80} height={20} />
                </div>
              </div>
            ))}
          </div>
        </ResponsiveCard>

        {/* System Status Skeleton */}
        <ResponsiveCard>
          <Skeleton variant="text" width="40%" className="h-6 mb-4" />
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex justify-between items-center">
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="text" width={80} height={24} />
              </div>
            ))}
          </div>
        </ResponsiveCard>
      </div>
    </div>
  );

  const renderSkeletonApplications = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Controls Skeleton */}
      <ResponsiveCard>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <Skeleton variant="button" width={100} height={40} />
            <Skeleton variant="button" width={180} height={40} />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <Skeleton variant="input" width={140} height={40} />
            <Skeleton variant="input" width={200} height={40} />
          </div>
        </div>
      </ResponsiveCard>

      {/* Applications List Skeleton */}
      <ResponsiveCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
          <div className="flex items-center justify-between">
            <Skeleton variant="text" width="40%" className="h-6" />
            <Skeleton variant="text" width={120} height={24} />
          </div>
        </div>

        <div className="space-y-4 p-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <ApplicationCardSkeleton key={index} />
          ))}
        </div>
      </ResponsiveCard>
    </div>
  );

  const renderSkeletonUsers = () => (
    <ResponsiveCard className="overflow-hidden">
      <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
        <Skeleton variant="text" width="40%" className="h-6" />
      </div>

      <div className="space-y-4 p-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <UserCardSkeleton key={index} />
        ))}
      </div>
    </ResponsiveCard>
  );

  const renderSkeletonJobs = () => (
    <div className="space-y-4">
      {/* Bulk Actions Skeleton */}
      <ResponsiveCard>
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="40%" className="h-6" />
            <Skeleton variant="text" width="60%" />
          </div>
          <Skeleton variant="button" width={200} height={48} />
        </div>
      </ResponsiveCard>

      {/* Jobs Filter and Stats Skeleton */}
      <ResponsiveCard>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <Skeleton variant="text" width={120} height={40} />
            <Skeleton variant="text" width={100} height={40} />
          </div>
          <div className="flex gap-2">
            <Skeleton variant="button" width={140} height={40} />
            <Skeleton variant="button" width={120} height={40} />
          </div>
        </div>
      </ResponsiveCard>

      {/* Jobs List Skeleton */}
      <ResponsiveCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
          <Skeleton variant="text" width="30%" className="h-6" />
        </div>

        <div className="space-y-4 p-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <JobCardSkeleton key={index} />
          ))}
        </div>
      </ResponsiveCard>
    </div>
  );

  const renderSkeletonAlerts = () => (
    <div className="space-y-4">
      {/* Alert Stats and Controls Skeleton */}
      <ResponsiveCard>
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="40%" className="h-6" />
            <Skeleton variant="text" width="60%" />
          </div>
          <Skeleton variant="button" width={160} height={48} />
        </div>
      </ResponsiveCard>

      {/* Alerts List Skeleton */}
      <ResponsiveCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500">
          <Skeleton variant="text" width="30%" className="h-6" />
        </div>

        <div className="space-y-4 p-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <AlertCardSkeleton key={index} />
          ))}
        </div>
      </ResponsiveCard>
    </div>
  );

  // ========== RENDER ==========

  if (loading) {
    return (
      <div
        className={`min-h-screen p-3 sm:p-4 md:p-6 transition-colors duration-300 ${
          isDark
            ? "bg-gray-900 text-white"
            : "bg-gradient-to-br from-green-50 to-sky-50 text-gray-900"
        }`}
      >
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="mb-4 sm:mb-6">
            <Skeleton variant="text" width="40%" className="h-8 mb-2" />
            <Skeleton variant="text" width="60%" />
          </div>

          {/* Tab Navigation Skeleton */}
          <div className="flex space-x-1 sm:space-x-2 mb-4 sm:mb-6 overflow-x-auto pb-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="button"
                className="h-10 flex-1 min-w-[120px]"
              />
            ))}
          </div>

          {/* Tab Content Skeleton */}
          {activeTab === "dashboard" && renderSkeletonDashboard()}
          {activeTab === "applications" && renderSkeletonApplications()}
          {activeTab === "users" && renderSkeletonUsers()}
          {activeTab === "jobs" && renderSkeletonJobs()}
          {activeTab === "alerts" && renderSkeletonAlerts()}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-3 sm:p-4 md:p-6 transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 text-white"
          : "bg-gradient-to-br from-green-50 to-sky-50 text-gray-900"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-600 to-sky-500 bg-clip-text text-transparent text-center sm:text-left">
            Admin Panel
          </h1>
          <p
            className={`text-sm sm:text-base text-center sm:text-left mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            Manage applications, jobs, users, and job alerts
          </p>
        </div>

        {/* Message Alert */}
        {message && (
          <div
            className={`p-3 mb-4 sm:mb-6 rounded-lg border text-sm ${
              message.includes("success") ||
              message.includes("updated") ||
              message.includes("sent")
                ? isDark
                  ? "bg-green-900/30 text-green-300 border-green-700"
                  : "bg-green-100 text-green-800 border-green-200"
                : isDark
                  ? "bg-red-900/30 text-red-300 border-red-700"
                  : "bg-red-100 text-red-800 border-red-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <span className="break-words flex-1">{message}</span>
              <button
                onClick={() => setMessage("")}
                className={`ml-2 flex-shrink-0 ${isDark ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"}`}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 sm:space-x-2 mb-4 sm:mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {["dashboard", "applications", "users", "jobs", "alerts"].map(
            (tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as ActiveTab)}
                className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap flex-1 min-w-[120px] text-sm sm:text-base ${
                  activeTab === tab
                    ? "bg-green-500 text-white shadow-md"
                    : isDark
                      ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                      : "bg-white text-gray-600 hover:bg-green-50 border border-gray-200"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span>
                    {tab === "dashboard"
                      ? "📊"
                      : tab === "applications"
                        ? "📋"
                        : tab === "users"
                          ? "👥"
                          : tab === "jobs"
                            ? "💼"
                            : "🔔"}
                  </span>
                  <span className="hidden xs:inline">
                    {tab === "dashboard"
                      ? "Dashboard"
                      : tab === "applications"
                        ? "Applications"
                        : tab === "users"
                          ? "User Management"
                          : tab === "jobs"
                            ? "Job Management"
                            : "Job Alerts"}
                  </span>
                </div>
              </button>
            ),
          )}
        </div>

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <ResponsiveGrid
              cols={{ mobile: 2, tablet: 2, desktop: 4 }}
              gap="gap-4"
            >
              <StatCard
                title="Total Users"
                value={dashboardStats.totalUsers}
                icon="👥"
                color="blue"
              />
              <StatCard
                title="Active Jobs"
                value={dashboardStats.totalJobs - dashboardStats.expiredJobs}
                icon="💼"
                color="green"
              />
              <StatCard
                title="Pending Applications"
                value={dashboardStats.pendingApplications}
                icon="📋"
                color="yellow"
              />
              <StatCard
                title="Active Alerts"
                value={dashboardStats.activeAlerts}
                icon="🔔"
                color="purple"
              />
            </ResponsiveGrid>

            {/* Additional Stats */}
            <ResponsiveGrid
              cols={{ mobile: 2, tablet: 2, desktop: 4 }}
              gap="gap-4"
            >
              <StatCard
                title="Total Applications"
                value={dashboardStats.totalApplications}
                icon="📝"
                color="blue"
              />
              <StatCard
                title="Active Users"
                value={dashboardStats.activeUsers}
                icon="✅"
                color="green"
              />
              <StatCard
                title="Expired Jobs"
                value={dashboardStats.expiredJobs}
                icon="⏰"
                color="red"
              />
              <StatCard
                title="Expiring Soon"
                value={dashboardStats.expiringSoonJobs}
                icon="⏳"
                color="yellow"
              />
            </ResponsiveGrid>

            {/* Quick Actions */}
            <ResponsiveCard>
              <h2 className="text-lg sm:text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => setActiveTab("applications")}
                  className="p-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium text-center"
                >
                  📋 Manage Applications
                </button>
                <button
                  onClick={() => setActiveTab("users")}
                  className="p-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium text-center"
                >
                  👥 Manage Users
                </button>
                <button
                  onClick={() => setActiveTab("jobs")}
                  className="p-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm font-medium text-center"
                >
                  💼 Manage Jobs
                </button>
                <button
                  onClick={() => setActiveTab("alerts")}
                  className="p-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium text-center"
                >
                  🔔 Manage Alerts
                </button>
              </div>
            </ResponsiveCard>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Applications */}
              <ResponsiveCard>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  Recent Applications
                </h3>
                <div className="space-y-3">
                  {allApplications.slice(0, 5).map((application) => (
                    <div
                      key={application.id}
                      onClick={() => handleApplicationClick(application.id)}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate text-gray-900 dark:text-white">
                          {application.user.name}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          Applied for {application.job.title}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(application.status)}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(application.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {allApplications.length === 0 && (
                    <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                      No applications yet
                    </p>
                  )}
                </div>
              </ResponsiveCard>

              {/* System Status */}
              <ResponsiveCard>
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                  System Status
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      API Status
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Operational
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Database
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Connected
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Email Service
                    </span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Active
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Storage
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      Healthy
                    </span>
                  </div>
                </div>
              </ResponsiveCard>
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === "applications" && (
          <div className="space-y-4 sm:space-y-6">
            {/* Controls */}
            <ResponsiveCard>
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                  <button
                    onClick={handleRefreshApplications}
                    disabled={isRefreshing}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                  >
                    {isRefreshing ? <LoadingSpinner size="sm" /> : "🔄"}
                    Refresh
                  </button>

                  {selectedApplications.size > 0 && (
                    <button
                      onClick={handleDeleteSelectedApplications}
                      disabled={isDeletingApplications}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {isDeletingApplications ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        "🗑️"
                      )}
                      Delete Selected ({selectedApplications.size})
                    </button>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
                  <select
                    value={applicationStatusFilter}
                    onChange={(e) =>
                      setApplicationStatusFilter(
                        e.target.value as ApplicationStatus,
                      )
                    }
                    className={`border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="reviewed">Under Review</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="withdrawn">Withdrawn</option>
                  </select>

                  <div className="relative flex-1 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="Search applications..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 text-sm ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                    <svg
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </ResponsiveCard>

            {/* Applications List */}
            <ResponsiveCard className="overflow-hidden">
              <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg sm:text-xl font-semibold text-white">
                    Job Applications ({filteredApplications.length})
                  </h2>
                  {filteredApplications.length > 0 && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="select-all-applications"
                        checked={
                          selectedApplications.size ===
                            filteredApplications.length &&
                          filteredApplications.length > 0
                        }
                        onChange={handleSelectAllApplications}
                        className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-400"
                      />
                      <label
                        htmlFor="select-all-applications"
                        className="text-white text-sm font-medium"
                      >
                        Select All
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {filteredApplications.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredApplications.map((application) => (
                    <div
                      key={application.id}
                      id={`application-${application.id}`}
                      className={`p-4 sm:p-6 transition-colors duration-200 ${
                        isDark ? "hover:bg-gray-750" : "hover:bg-green-50"
                      } ${selectedApplications.has(application.id) ? (isDark ? "bg-blue-900/20" : "bg-blue-50") : ""}`}
                    >
                      <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {application.job.title}
                              </h3>
                              <p className="text-green-600 dark:text-green-400">
                                {application.job.company} •{" "}
                                {application.job.location}
                              </p>
                            </div>
                            {getStatusBadge(application.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <strong>Applicant:</strong>{" "}
                                {application.user.name}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <strong>Email:</strong> {application.user.email}
                              </p>
                              {application.user.profile?.phone && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  <strong>Phone:</strong>{" "}
                                  {application.user.profile.phone}
                                </p>
                              )}
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <strong>Applied:</strong>{" "}
                                {formatDateTime(application.createdAt)}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                <strong>Updated:</strong>{" "}
                                {formatDateTime(application.updatedAt)}
                              </p>
                            </div>
                          </div>

                          {application.coverLetter && (
                            <div className="mb-3">
                              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                <strong>Cover Letter:</strong>{" "}
                                {application.coverLetter}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-3 min-w-[250px]">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedApplications.has(application.id)}
                              onChange={() =>
                                handleSelectApplication(application.id)
                              }
                              className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-400"
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Select
                            </span>
                          </div>

                          <select
                            value={application.status}
                            onChange={(e) =>
                              handleUpdateApplicationStatus(
                                application.id,
                                e.target.value,
                              )
                            }
                            disabled={updatingStatus === application.id}
                            className={`border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-white"
                                : "bg-white border-gray-300 text-gray-900"
                            }`}
                          >
                            <option value="pending">Pending Review</option>
                            <option value="reviewed">Under Review</option>
                            <option value="accepted">Accepted</option>
                            <option value="rejected">Not Selected</option>
                            <option value="withdrawn">Withdrawn</option>
                          </select>

                          <div className="flex gap-2">
                            {application.resumeUrl && (
                              <button
                                onClick={() => {
                                  openResume(
                                    application.resumeUrl!,
                                    application.user.name,
                                  );
                                }}
                                className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                              >
                                View Resume
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleOpenContactModal(application)
                              }
                              className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                            >
                              Contact
                            </button>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() =>
                                handleScheduleInterview(application)
                              }
                              className="flex-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                            >
                              Schedule
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteSingleApplication(application.id)
                              }
                              className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4 text-gray-400 dark:text-gray-600">
                    {searchTerm || applicationStatusFilter !== "all"
                      ? "🔍"
                      : "📝"}
                  </div>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-2">
                    {searchTerm || applicationStatusFilter !== "all"
                      ? "No matching applications found"
                      : "No applications found"}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md mx-auto">
                    {searchTerm || applicationStatusFilter !== "all"
                      ? "Try adjusting your search terms or filters to find what you're looking for."
                      : "Applications will appear here when users start applying to jobs."}
                  </p>
                </div>
              )}
            </ResponsiveCard>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <ResponsiveCard className="overflow-hidden">
            <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
              <h2 className="text-lg sm:text-xl font-semibold text-white">
                User Management ({users.length})
              </h2>
            </div>

            {users.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {user.name}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                              {user.email}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span
                              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                user.role === "admin"
                                  ? "bg-gradient-to-r from-green-500 to-sky-500 text-white"
                                  : isDark
                                    ? "bg-green-900/50 text-green-300"
                                    : "bg-green-100 text-green-800"
                              }`}
                            >
                              {user.role}
                            </span>
                            <span
                              className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                user.active
                                  ? isDark
                                    ? "bg-green-900/50 text-green-300"
                                    : "bg-green-100 text-green-800"
                                  : isDark
                                    ? "bg-red-900/50 text-red-300"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {user.active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>
                            <p>
                              <strong>User ID:</strong> {user.id.slice(0, 8)}...
                            </p>
                            <p>
                              <strong>Created:</strong>{" "}
                              {user.createdAt
                                ? formatDate(user.createdAt)
                                : "Unknown"}
                            </p>
                          </div>
                          <div>
                            <p>
                              <strong>Subscription:</strong>{" "}
                              {user.subscribed
                                ? "Subscribed"
                                : "Not Subscribed"}
                            </p>
                            {user.deactivatedAt && (
                              <p>
                                <strong>Deactivated:</strong>{" "}
                                {formatDate(user.deactivatedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3 min-w-[200px]">
                        <select
                          value={user.role}
                          onChange={(e) =>
                            handleUpdateUserRole(user.id, e.target.value)
                          }
                          disabled={
                            updatingRoles.has(user.id) ||
                            user.id === currentUser?.id
                          }
                          className={`border rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>

                        <div className="flex gap-2">
                          {user.active ? (
                            <button
                              onClick={() => handleDeactivateUser(user.id)}
                              disabled={
                                deactivatingUsers.has(user.id) ||
                                user.id === currentUser?.id
                              }
                              className="flex-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {deactivatingUsers.has(user.id) ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                "Deactivate"
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivateUser(user.id)}
                              disabled={activatingUsers.has(user.id)}
                              className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {activatingUsers.has(user.id) ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                "Activate"
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={
                              deletingUsers.has(user.id) ||
                              user.id === currentUser?.id
                            }
                            className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {deletingUsers.has(user.id) ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              "Delete"
                            )}
                          </button>
                        </div>

                        {user.id === currentUser?.id && (
                          <p className="text-xs text-red-500 text-center">
                            Cannot modify your own account
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4 text-gray-400 dark:text-gray-600">
                  👥
                </div>
                <p className="text-lg text-gray-500 dark:text-gray-400">
                  No users found
                </p>
              </div>
            )}
          </ResponsiveCard>
        )}

        {/* Jobs Tab */}
        {activeTab === "jobs" && (
          <div className="space-y-4">
            {/* Bulk Actions */}
            <ResponsiveCard>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                    Bulk Job Alerts
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Trigger job alerts for all active jobs to notify users with
                    matching alerts
                  </p>
                </div>
                <button
                  onClick={handleTriggerBulkJobAlerts}
                  disabled={bulkTriggering || jobs.length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {bulkTriggering ? <LoadingSpinner size="sm" /> : "🔔"}
                  Trigger All Job Alerts
                </button>
              </div>
            </ResponsiveCard>

            {/* Jobs Filter and Stats */}
            <ResponsiveCard>
              <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <span className="text-yellow-600 dark:text-yellow-400 font-semibold">
                      ⏳ Expiring Soon: {dashboardStats.expiringSoonJobs}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <span className="text-red-600 dark:text-red-400 font-semibold">
                      ⚠️ Expired: {dashboardStats.expiredJobs}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      // Filter to show only expiring soon jobs
                      const expiringJobs = jobs.filter(
                        (job) =>
                          job.deadline && isJobExpiringSoon(job.deadline),
                      );
                      setJobs(expiringJobs);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                  >
                    ⏳ Show Expiring Soon
                  </button>
                  <button
                    onClick={async () => {
                      // Reset to show all jobs
                      const jobsData = await fetchJobs();
                      setJobs(jobsData.jobs || []);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  >
                    🔄 Show All Jobs
                  </button>
                </div>
              </div>
            </ResponsiveCard>

            {/* Jobs List */}
            <ResponsiveCard className="overflow-hidden">
              <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-green-500 to-sky-500">
                <h2 className="text-lg sm:text-xl font-semibold text-white">
                  All Jobs ({jobs.length})
                </h2>
              </div>

              {jobs.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {jobs.map((job) => {
                    const isExpired =
                      job.deadline && new Date(job.deadline) < new Date();
                    const expiresSoon =
                      job.deadline && isJobExpiringSoon(job.deadline);
                    const daysLeft = getDaysUntilDeadline(job.deadline);

                    return (
                      <div
                        key={job.id}
                        className={`p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                          expiresSoon
                            ? "bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500"
                            : ""
                        } ${isExpired ? "bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500" : ""}`}
                      >
                        <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
                          <div className="flex items-start gap-4 flex-1">
                            {job.imageUrl && (
                              <img
                                src={job.imageUrl}
                                alt={`${job.company} logo`}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {job.title}
                                  </h3>
                                  <p className="text-lg text-green-600 dark:text-green-400">
                                    {job.company} • {job.location}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  {isExpired && (
                                    <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-semibold rounded-full">
                                      Expired
                                    </span>
                                  )}
                                  {expiresSoon && !isExpired && (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-semibold rounded-full">
                                      Expiring Soon
                                    </span>
                                  )}
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {job._count?.jobApplications || 0}{" "}
                                    applications
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3 text-sm text-gray-600 dark:text-gray-400">
                                <div>
                                  <p>
                                    <strong>Salary:</strong> {job.salary}
                                  </p>
                                  <p>
                                    <strong>Type:</strong>{" "}
                                    {job.type.replace("_", " ")}
                                  </p>
                                </div>
                                <div>
                                  <p
                                    className={
                                      expiresSoon
                                        ? "text-yellow-600 dark:text-yellow-400 font-semibold"
                                        : ""
                                    }
                                  >
                                    <strong>Deadline:</strong>{" "}
                                    {formatDate(job.deadline)}
                                    {expiresSoon && " ⏳"}
                                  </p>
                                  <p>
                                    <strong>Posted:</strong>{" "}
                                    {formatDate(job.createdAt)}
                                  </p>
                                </div>
                                <div>
                                  <p>
                                    <strong>Days Left:</strong>{" "}
                                    {daysLeft >= 0 ? daysLeft : "Expired"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-3 min-w-[200px]">
                            <button
                              onClick={() => handleTriggerJobAlerts(job.id)}
                              disabled={
                                !!(triggeringAlerts.has(job.id) || isExpired)
                              }
                              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {triggeringAlerts.has(job.id) ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                "Trigger Alerts"
                              )}
                            </button>
                            <button
                              onClick={() => handleEditJob(job.id)}
                              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            >
                              Edit Job
                            </button>
                            <button
                              onClick={() => handleDeleteJob(job.id)}
                              disabled={deletingJobs.has(job.id)}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {deletingJobs.has(job.id) ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                "Delete Job"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4 text-gray-400 dark:text-gray-600">
                    💼
                  </div>
                  <p className="text-lg text-gray-500 dark:text-gray-400">
                    No jobs found
                  </p>
                </div>
              )}
            </ResponsiveCard>
          </div>
        )}

        {/* Job Alerts Tab */}
        {activeTab === "alerts" && (
          <div className="space-y-4">
            {/* Alert Stats and Controls */}
            <ResponsiveCard>
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">
                    Job Alerts Management
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    View and manage all job alerts created by users
                  </p>
                </div>
                <button
                  onClick={handleRefreshJobAlerts}
                  disabled={refreshingAlerts}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {refreshingAlerts ? <LoadingSpinner size="sm" /> : "🔄"}
                  Refresh Alerts
                </button>
              </div>
            </ResponsiveCard>

            {/* Alerts List */}
            <ResponsiveCard className="overflow-hidden">
              <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500">
                <h2 className="text-lg sm:text-xl font-semibold text-white">
                  Job Alerts ({jobAlerts.length})
                </h2>
              </div>

              {jobAlerts.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {jobAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    >
                      <div className="flex flex-col lg:flex-row gap-4 items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {alert.name}
                              </h3>
                              <p className="text-purple-600 dark:text-purple-400">
                                by {alert.user.name} • {alert.user.email}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span
                                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                  alert.isActive
                                    ? isDark
                                      ? "bg-green-900/50 text-green-300"
                                      : "bg-green-100 text-green-800"
                                    : isDark
                                      ? "bg-red-900/50 text-red-300"
                                      : "bg-red-100 text-red-800"
                                }`}
                              >
                                {alert.isActive ? "Active" : "Inactive"}
                              </span>
                              <span
                                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                  isDark
                                    ? "bg-purple-900/50 text-purple-300"
                                    : "bg-purple-100 text-purple-800"
                                }`}
                              >
                                {alert.frequency}
                              </span>
                              <span
                                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                  isDark
                                    ? "bg-blue-900/50 text-blue-300"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {alert.triggerCount} triggers
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <div>
                              <p>
                                <strong>Category:</strong>{" "}
                                {alert.category || "Any"}
                              </p>
                              <p>
                                <strong>Job Type:</strong>{" "}
                                {alert.jobType || "Any"}
                              </p>
                              <p>
                                <strong>Location:</strong>{" "}
                                {alert.location || "Any"}
                              </p>
                            </div>
                            <div>
                              <p>
                                <strong>Keywords:</strong>{" "}
                                {formatKeywords(alert.keywords)}
                              </p>
                              <p>
                                <strong>Created:</strong>{" "}
                                {formatDate(alert.createdAt)}
                              </p>
                              {alert.lastTriggered && (
                                <p>
                                  <strong>Last Triggered:</strong>{" "}
                                  {formatDateTime(alert.lastTriggered)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-3 min-w-[200px]">
                          <button
                            onClick={() =>
                              handleUpdateJobAlertStatus(
                                alert.id,
                                !alert.isActive,
                              )
                            }
                            disabled={updatingAlerts.has(alert.id)}
                            className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                              alert.isActive
                                ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                : "bg-green-500 text-white hover:bg-green-600"
                            } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                          >
                            {updatingAlerts.has(alert.id) ? (
                              <LoadingSpinner size="sm" />
                            ) : alert.isActive ? (
                              "Deactivate"
                            ) : (
                              "Activate"
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteJobAlert(alert.id)}
                            disabled={deletingAlerts.has(alert.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            {deletingAlerts.has(alert.id) ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              "Delete Alert"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4 text-gray-400 dark:text-gray-600">
                    🔔
                  </div>
                  <p className="text-lg text-gray-500 dark:text-gray-400">
                    No job alerts found
                  </p>
                </div>
              )}
            </ResponsiveCard>
          </div>
        )}
      </div>

      {/* Modals */}
      <ContactApplicantModal
        isOpen={contactModalOpen}
        onClose={handleCloseContactModal}
        application={selectedApplication}
        onContact={handleContactApplicant}
        currentUser={currentUser}
      />

      <ViewResumeModal
        resumeUrl={currentResumeUrl}
        isOpen={isResumeModalOpen}
        onClose={closeResume}
        applicantName={currentApplicantName}
      />

      <InterviewSchedulingModal
        isOpen={isInterviewModalOpen}
        onClose={handleCloseInterviewModal}
        application={selectedApplication || undefined}
        onSchedule={handleInterviewSchedule}
        isScheduling={isSchedulingInterview}
      />
    </div>
  );
}
